import type { ComputedRef, Ref } from 'vue';

import type { FleequidStatsResponseDto } from '~/apiClient';

type RoundWithPrecisionParams = Parameters<typeof roundWithPrecision>;
type RoundingParams = [precision: RoundWithPrecisionParams[1], mode?: RoundWithPrecisionParams[2]];

type UseFleequidStatsReturn = {
    soldVehicles: ComputedRef<number>;
    countries: ComputedRef<number>;
    companies: ComputedRef<number>;
    availableVehicles: ComputedRef<number>;
    busAssets: ComputedRef<number>;
    /** @deprecated BSD-4645: alias of `busAssets`, kept only so legacy `%busDossiers%` Storyblok tokens keep resolving. */
    busDossiers: ComputedRef<number>;
    employees: ComputedRef<number>;
    _raw: {
        apiStats: Ref<FleequidStatsResponseDto | null>;
    };
};

const FALLBACK_API_STATS: FleequidStatsResponseDto = {
    soldVehicles: 1500,
    countries: 70,
    companies: 4000,
    availableVehicles: 700,
    busAssets: 3000,
};

export const useFleequidStats = (): UseFleequidStatsReturn => {
    const apiStats = useState<FleequidStatsResponseDto | null>('fleequidStats', () => null);

    const roundedStat = (key: keyof FleequidStatsResponseDto, ...roundingArgs: RoundingParams): ComputedRef<number> =>
        computed(() => roundWithPrecision(apiStats.value?.[key] ?? FALLBACK_API_STATS[key], ...roundingArgs));

    // BSD-4525: rounded up like soldVehicles, deliberately kept as-is with the "Oltre {count}" copy.
    // BSD-4645: the figure now counts every vehicle onboarded on the platform (bus assets) instead of the
    // bus dossiers, which ignored everything onboarded before the dossier flow existed.
    const busAssets = roundedStat('busAssets', 100, 'ceil');

    return {
        soldVehicles: roundedStat('soldVehicles', 100, 'ceil'),
        countries: roundedStat('countries', 10),
        companies: roundedStat('companies', 100, 'ceil'),
        // Currently unused due to a tragicomic misunderstanding (see: https://adorea.atlassian.net/browse/PT-452?focusedCommentId=37295)
        availableVehicles: roundedStat('availableVehicles', 100, 'floor'),
        busAssets,
        busDossiers: busAssets,

        employees: computed(() => 70),

        // For exclusive use of the fleequidStats plugin
        _raw: {
            apiStats,
        },
    };
};
